// src/pages/Areas.tsx
import { Link } from "react-router-dom";

function SEO() {
  const title = "Areas We Cover | Beaumonts Plumbing & Heating, Lincoln";
  const description =
    "Plumbers in Lincoln covering LN1–LN6 and nearby villages including North Hykeham, Waddington, Nettleham, Skellingthorpe and Bracebridge Heath.";
  const href = typeof window !== "undefined" ? window.location.href : "";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Plumbing & Heating",
    "provider": { "@type": "LocalBusiness", "name": "Beaumonts Plumbing & Heating" },
    "areaServed": [
      "Lincoln", "LN1", "LN2", "LN3", "LN4", "LN5", "LN6",
      "North Hykeham", "Waddington", "Nettleham", "Skellingthorpe", "Bracebridge Heath"
    ],
  };

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={href} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </>
  );
}

const POSTCODES = ["LN1", "LN2", "LN3", "LN4", "LN5", "LN6", "LN7", "NG"];
const VILLAGES = ["North Hykeham", "Waddington", "Nettleham", "Skellingthorpe", "Bracebridge Heath", "Many more..."];

export default function Areas() {
  return (
    <>
      <SEO />

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14 md:py-20">
        <div className="max-w-3xl">
          <p className="text-xs font-medium tracking-widest uppercase text-[#3bb3ef]">Areas we cover</p>
          <h1 className="mt-2 text-3xl md:text-5xl font-semibold tracking-[-0.02em]">
            Plumbers in Lincoln and nearby
          </h1>
          <p className="mt-3 text-[15px] md:text-base text-neutral-700">
            We work across Lincolnshire with clear pricing, tidy installs and dependable aftercare.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center rounded-md bg-[#3bb3ef] text-white px-5 py-3 text-sm font-medium hover:brightness-110"
            >
              Request a quote
            </Link>
            <a
              href="tel:+441522692592"
              className="inline-flex items-center rounded-md border border-neutral-300 px-5 py-3 text-sm font-medium hover:bg-neutral-50"
            >
              Call 01522 692592
            </a>
          </div>
        </div>
      </section>

      {/* Map + coverage */}
      <section className="relative border-t border-neutral-200 bg-white">
        {/* soft radial for depth */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_40%_at_50%_0%,#f4f7fb_0%,transparent_60%)]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-12 md:py-16 grid lg:grid-cols-12 gap-8">
          {/* Map */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-sm">
              {/* Lightweight Google Maps embed centered on Lincoln */}
              <iframe
                title="Lincoln area map"
                src="https://www.google.com/maps?q=Lincoln,UK&z=11&output=embed"
                className="w-full h-[360px] md:h-[480px] border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-neutral-600">
              <a
                className="underline underline-offset-4 hover:no-underline"
                href="https://www.google.com/maps/dir/?api=1&destination=Lincoln"
                target="_blank" rel="noreferrer"
              >
                Open directions
              </a>
              <span>•</span>
              <a
                className="underline underline-offset-4 hover:no-underline"
                href="https://www.google.com/maps/search/?api=1&query=plumbers+in+Lincoln"
                target="_blank" rel="noreferrer"
              >
                View nearby
              </a>
            </div>
          </div>

          {/* Coverage list */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-[0_1px_0_0_rgba(0,0,0,0.03)]">
              <h2 className="text-lg md:text-xl font-semibold">Coverage</h2>
              <p className="mt-2 text-sm text-neutral-700">We cover Lincoln city and the surrounding villages.</p>

              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium">Postcodes</div>
                  <ul className="mt-2 grid grid-cols-3 sm:grid-cols-2 gap-2 text-sm text-neutral-700">
                    {POSTCODES.map(pc => (
                      <li key={pc} className="rounded-md border border-neutral-200 px-2 py-1 text-center bg-white">{pc}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-sm font-medium">Villages</div>
                  <ul className="mt-2 grid grid-cols-1 gap-2 text-sm text-neutral-700">
                    {VILLAGES.map(v => (
                      <li key={v} className="rounded-md border border-neutral-200 px-2 py-1 bg-white">{v}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-md bg-[#3bb3ef] text-white px-4 py-2.5 text-sm font-medium hover:brightness-110"
                >
                  Check availability
                </Link>
                <a
                  href="tel:+441522692592"
                  className="inline-flex items-center rounded-md border border-neutral-300 px-4 py-2.5 text-sm font-medium hover:bg-neutral-50"
                >
                  Call 01522 692592
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Micro FAQs for the page */}
      <section className="py-12 md:py-16 bg-white border-t border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <h3 className="text-lg md:text-2xl font-semibold tracking-tight">Good to know</h3>
          <div className="mt-6 grid md:grid-cols-3 gap-4 text-sm">
            {[
              ["Hours", "Mon–Fri 8:00–18:00, Sat–Sun 9:00–13:00"],
              ["Parking & access", "We’ll confirm access, parking and protection ahead of time."],
              ["Emergencies", "We’ll always try to fit you in. Call and we’ll give you the earliest slot."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-xl border border-neutral-200 bg-white p-5">
                <div className="font-medium">{t}</div>
                <div className="mt-1 text-neutral-700">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-14 bg-gradient-to-r from-[#3bb3ef] to-[#1f8fd3] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
          <div>
            <h3 className="text-lg md:text-xl font-semibold">In our area? Let’s get your job booked.</h3>
            <p className="opacity-90 text-[13px] md:text-sm">Tell us what you need and we’ll confirm dates and pricing.</p>
          </div>
          <div className="flex gap-2 md:gap-3">
            <a
              href="tel:+441522692592"
              className="inline-flex items-center rounded-md bg-white text-[#187fbf] px-4 md:px-5 py-2.5 text-[13px] md:text-sm font-medium hover:brightness-95"
            >
              Call 01522 692592
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center rounded-md border border-white/80 px-4 md:px-5 py-2.5 text-[13px] md:text-sm font-medium hover:bg-white/10"
            >
              Request a quote
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
